/**
 * lifecycle.js — thin, testable subscribers for native app lifecycle
 * events. Wraps the Capacitor `App` plugin on iOS / Android and falls
 * back to browser `visibilitychange` / `focus` / `blur` events on web
 * so consumers can write a single "on resume, do X" hook regardless of
 * runtime.
 *
 * Events surfaced:
 *   - `state`   → `{ isActive: boolean }` — foreground / background
 *   - `resume`  → convenience alias fired when isActive flips true
 *   - `pause`   → convenience alias fired when isActive flips false
 *   - `url`     → `{ url }` — deep link opened (cold + warm start)
 *   - `back`    → Android hardware back button. Listener callback
 *                 returns `true` to signal it handled the press; the
 *                 outermost registered handler wins (LIFO).
 *
 * Registration returns an unsubscribe function so React effects can
 * clean up idempotently and avoid the "listener leak on every rerender"
 * class of bug the audit called out.
 */
import { isNative, isAndroid } from './platform.js';

let _app = null;

async function appPlugin() {
  if (_app) return _app;
  const mod = await import('@capacitor/app');
  _app = mod.App;
  return _app;
}

// ────────────────────────────────────────────────────────────────
// State (foreground / background)
// ────────────────────────────────────────────────────────────────

/**
 * @param {(state: { isActive: boolean }) => void} handler
 * @returns {() => void} unsubscribe
 */
export function onAppState(handler) {
  if (typeof handler !== 'function') return () => {};

  if (isNative()) {
    let cleanup = () => {};
    let cancelled = false;
    (async () => {
      try {
        const app = await appPlugin();
        const sub = await app.addListener('appStateChange', handler);
        if (cancelled) { sub.remove(); return; }
        cleanup = () => { try { sub.remove(); } catch { /* ignore */ } };
      } catch { /* plugin not available — leave cleanup as no-op */ }
    })();
    return () => { cancelled = true; cleanup(); };
  }

  // Web fallback — pageshow / pagehide + visibilitychange model.
  if (typeof document === 'undefined') return () => {};
  const emit = () => handler({ isActive: !document.hidden });
  document.addEventListener('visibilitychange', emit);
  window.addEventListener('focus', emit);
  window.addEventListener('blur', emit);
  return () => {
    document.removeEventListener('visibilitychange', emit);
    window.removeEventListener('focus', emit);
    window.removeEventListener('blur', emit);
  };
}

/** Convenience: fire once each time the app returns to the foreground. */
export function onResume(handler) {
  return onAppState(({ isActive }) => { if (isActive) handler(); });
}

/** Convenience: fire once each time the app leaves the foreground. */
export function onPause(handler) {
  return onAppState(({ isActive }) => { if (!isActive) handler(); });
}

// ────────────────────────────────────────────────────────────────
// Deep links
// ────────────────────────────────────────────────────────────────

/**
 * Subscribe to `appUrlOpen` events (deep-link taps while the app is
 * running). Web has no equivalent — this is a no-op there.
 *
 * @param {(event: { url: string }) => void} handler
 */
export function onAppUrlOpen(handler) {
  if (typeof handler !== 'function' || !isNative()) return () => {};
  let cleanup = () => {};
  let cancelled = false;
  (async () => {
    try {
      const app = await appPlugin();
      const sub = await app.addListener('appUrlOpen', handler);
      if (cancelled) { sub.remove(); return; }
      cleanup = () => { try { sub.remove(); } catch { /* ignore */ } };
    } catch { /* ignore */ }
  })();
  return () => { cancelled = true; cleanup(); };
}

/**
 * Resolve the URL that launched the app during a cold start. Returns
 * `null` when the app was opened normally (icon tap) or on web.
 */
export async function getLaunchUrl() {
  if (!isNative()) return null;
  try {
    const app = await appPlugin();
    const result = await app.getLaunchUrl();
    return result?.url || null;
  } catch { return null; }
}

// ────────────────────────────────────────────────────────────────
// Android back button
// ────────────────────────────────────────────────────────────────

// A single listener installed on the plugin talks to a chain of
// consumer-registered handlers. Handlers are consulted in LIFO order
// (newest first); the first one to return `true` swallows the press.
// This is the pattern the audit called out — one native listener,
// many React consumers, and no leak on rerender.

const backHandlers = [];
let backListenerHandle = null;
let backListenerInstalling = false;

async function installBackListener() {
  if (!isAndroid() || backListenerHandle || backListenerInstalling) return;
  backListenerInstalling = true;
  try {
    const app = await appPlugin();
    backListenerHandle = await app.addListener('backButton', ({ canGoBack }) => {
      for (let i = backHandlers.length - 1; i >= 0; i--) {
        const fn = backHandlers[i];
        let handled = false;
        try { handled = fn({ canGoBack }) === true; } catch { handled = false; }
        if (handled) return;
      }
      // No consumer handled the press. Defer to the default behaviour:
      // navigate back if the WebView has history, otherwise minimise
      // the app (never `exitApp` — that's a jarring UX and users lose
      // in-flight charging state).
      if (canGoBack) {
        try { window.history.back(); } catch { /* ignore */ }
      } else {
        try { app.minimizeApp(); } catch { /* ignore */ }
      }
    });
  } catch { /* plugin missing — silently no-op */ }
  finally { backListenerInstalling = false; }
}

/**
 * Register an Android back-button handler. The most recently registered
 * handler runs first; returning `true` marks the press as consumed so
 * older handlers don't run. Returns an unsubscribe function.
 *
 * On non-Android runtimes this is a no-op and returns a no-op cleanup.
 */
export function onBackButton(handler) {
  if (typeof handler !== 'function' || !isAndroid()) return () => {};
  backHandlers.push(handler);
  installBackListener();
  return () => {
    const idx = backHandlers.indexOf(handler);
    if (idx >= 0) backHandlers.splice(idx, 1);
  };
}

// ────────────────────────────────────────────────────────────────
// Test-only reset — lets test suites clear registered listeners
// between cases without touching module private state directly.
// ────────────────────────────────────────────────────────────────
export const _INTERNAL = {
  reset() {
    backHandlers.length = 0;
    if (backListenerHandle && typeof backListenerHandle.remove === 'function') {
      try { backListenerHandle.remove(); } catch { /* ignore */ }
    }
    backListenerHandle = null;
    backListenerInstalling = false;
  },
};
